import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Conversation, Message, MessageType, ConversationType, TypingRequest, ConversationMember } from '../../types/messaging'
import { messagingService } from '../../services/messagingService'
import { storageService } from '../../services/storageService'
import { MessageBubble } from './MessageBubble'
import { MessageInput } from './MessageInput'
import { TypingIndicator } from './TypingIndicator'
import { GroupMembersDisplay } from './GroupMembersDisplay'
import { MemberManagement } from './MemberManagement'
import { useMessageReactions } from '../../hooks/useMessageReactions'
import { cn } from '../../lib/utils'
import { 
  ArrowLeft, 
  MoreVertical, 
  Search,
  Users,
  Settings,
  Info
} from 'lucide-react'

interface ChatInterfaceProps {
  conversation: Conversation
  messages: Message[]
  typingUsers: TypingRequest[]
  currentUserId: number
  onBack?: () => void
  onSendMessage?: (conversationId: number, content: string, messageType: any, attachmentUrls?: string[], replyToMessageId?: number) => Promise<Message | void>
  onTyping?: (isTyping: boolean) => void
  onMarkAsRead?: (conversationId: number, messageId: number) => void
  wsConnected?: boolean
  className?: string
}

export const ChatInterface: React.FC<ChatInterfaceProps> = ({
  conversation,
  messages: propMessages,
  typingUsers: propTypingUsers,
  currentUserId,
  onBack,
  onSendMessage: propOnSendMessage,
  onTyping: propOnTyping,
  onMarkAsRead: propOnMarkAsRead,
  wsConnected,
  className
}) => {
  const navigate = useNavigate()
  const [messages, setMessages] = useState<Message[]>([])
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [page, setPage] = useState(0)
  const [hasMore, setHasMore] = useState(true)
  const [showMembersModal, setShowMembersModal] = useState(false)
  const [groupMembers, setGroupMembers] = useState<ConversationMember[]>(conversation.members || [])
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const messagesContainerRef = useRef<HTMLDivElement>(null)

  // Initialize message reactions hook
  const {
    reactionCounts,
    currentUserReactions,
    isReacting,
    reactToMessage: handleReactToMessage,
    removeReaction: handleRemoveReaction,
    setInitialReactionsBatch
  } = useMessageReactions()

  useEffect(() => {
    if (propMessages) {
      setMessages(propMessages)
      setLoading(false)
      // Initialize reactions from messages
      const reactionsData: Record<number, any> = {}
      const userReactionsData: Record<number, 'like' | 'love' | 'wow' | 'sad' | 'angry' | 'haha' | null> = {}
      propMessages.forEach(msg => {
        if (msg.reactionCounts) {
          reactionsData[msg.id] = msg.reactionCounts
        }
        if (msg.currentUserReaction !== undefined) {
          userReactionsData[msg.id] = msg.currentUserReaction
        }
      })
      setInitialReactionsBatch(reactionsData, userReactionsData)
    } else {
      loadMessages()
    }
  }, [conversation.id, propMessages, setInitialReactionsBatch])

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  // Mark messages as read when viewing the conversation
  useEffect(() => {
    if (!messages || messages.length === 0) return
    
    // Find all unread messages from other users
    const unreadMessages = messages.filter(
      msg => msg.senderId !== currentUserId && !msg.isRead
    )
    
    if (unreadMessages.length === 0) return
    
    // Mark the latest unread message as read (this will mark all previous as read on backend)
    const latestUnread = unreadMessages[unreadMessages.length - 1]
    
    // Delay marking as read slightly to ensure user is viewing the message
    const timer = setTimeout(() => {
      if (propOnMarkAsRead) {
        console.log('📖 Marking message as read:', {
          conversationId: conversation.id,
          messageId: latestUnread.id,
          unreadCount: unreadMessages.length
        })
        propOnMarkAsRead(conversation.id, latestUnread.id)
      }
    }, 500) // 500ms delay to ensure user is viewing
    
    return () => clearTimeout(timer)
  }, [messages, currentUserId, conversation.id, propOnMarkAsRead])

  useEffect(() => {
    console.log('Conversation data:', conversation);
    if (conversation.members && conversation.members.length > 0) {
      setGroupMembers(conversation.members)
    }
  }, [conversation]);

  useEffect(() => {
    if (conversation.type === ConversationType.GROUP && groupMembers.length === 0) {
      loadConversationMembers()
    }
  }, [conversation.id, conversation.type])

  const loadConversationMembers = async () => {
    if (conversation.type !== ConversationType.GROUP) return
    
    try {
      const response = await messagingService.getConversationMembers(conversation.id, {
        page: 0,
        size: 100
      })
      setGroupMembers(response.members)
    } catch (error) {
      console.error('Failed to load conversation members:', error)
    }
  }

  const loadMessages = async (pageNum = 0) => {
    try {
      setLoading(true)
      const response = await messagingService.getConversationMessages(
        conversation.id,
        { page: pageNum, size: 50 }
      )
      
      const reversedMessages = (response.messages || []).reverse()
      
      if (pageNum === 0) {
        setMessages(reversedMessages)
      } else {
        setMessages(prev => [...reversedMessages, ...prev])
      }
      
      // Initialize reactions from loaded messages
      const reactionsData: Record<number, any> = {}
      const userReactionsData: Record<number, 'like' | 'love' | 'wow' | 'sad' | 'angry' | 'haha' | null> = {}
      reversedMessages.forEach(msg => {
        if (msg.reactionCounts) {
          reactionsData[msg.id] = msg.reactionCounts
        }
        if (msg.currentUserReaction !== undefined) {
          userReactionsData[msg.id] = msg.currentUserReaction
        }
      })
      setInitialReactionsBatch(reactionsData, userReactionsData)
      
      setHasMore(response.hasNext)
      setPage(pageNum)
    } catch (error) {
      console.error('Failed to load messages:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadMoreMessages = () => {
    if (hasMore && !loading) {
      loadMessages(page + 1)
    }
  }

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  const handleSendMessage = async (content: string, messageType: MessageType, attachments: string[], _replyToMessageId?: number, files?: File[]) => {
    if (!content.trim() && (!files || files.length === 0) && attachments.length === 0) return

    try {
      setSending(true)

      let attachmentUrls: string[] = []
      if (files && files.length > 0) {
        const urls = await storageService.uploadFiles(files)
        attachmentUrls = urls
      } else if (attachments && attachments.length > 0) {
        attachmentUrls = attachments.filter(url => !url.startsWith('blob:'))
      }

      // Determine type: backend may infer, but we provide a hint
      const typeHint = attachmentUrls.length > 0
        ? storageService.guessMessageTypeFromUrls(attachmentUrls)
        : 'text'

      // Use hook's sendMessage if provided, otherwise call service directly
      if (propOnSendMessage) {
        // Hook will handle message state updates (via WebSocket or state)
        // If WebSocket: message will come via broadcast
        // If REST API fallback: hook returns the message
        const result = await propOnSendMessage(conversation.id, content.trim(), typeHint as any, attachmentUrls, _replyToMessageId)
        
        // If result is returned (REST API fallback with no WebSocket), 
        // and WebSocket is not connected, append it
        if (result && !wsConnected) {
          setMessages(prev => [...prev, result])
        }
        // If WebSocket is connected, message will arrive via broadcast - don't append here
      } else {
        // Direct service call - message will be added via WebSocket broadcast
        // Don't append here to avoid duplicates when WebSocket is active
        const newMessage = await messagingService.sendMessage({
          conversationId: conversation.id,
          content: content.trim(),
          messageType: (typeHint as any),
          attachmentUrls,
          replyToMessageId: _replyToMessageId
        })
        
        // Only append if WebSocket is NOT connected (fallback mode)
        if (!wsConnected) {
          setMessages(prev => [...prev, newMessage])
        }
        // If WebSocket is connected, message will come via WebSocket broadcast
      }
      
      // Mark as read after sending
      if (propOnMarkAsRead) {
        // Will be marked after message arrives
      }
    } catch (error) {
      console.error('Failed to send message:', error)
    } finally {
      setSending(false)
    }
  }

  const handleEditMessage = async (message: Message) => {
    const newContent = prompt('Edit message:', message.content)
    if (newContent && newContent !== message.content) {
      try {
        const updatedMessage = await messagingService.editMessage(message.id, {
          content: newContent
        })
        
        setMessages(prev => prev.map(m => 
          m.id === message.id ? updatedMessage : m
        ))
      } catch (error) {
        console.error('Failed to edit message:', error)
      }
    }
  }

  const handleDeleteMessage = async (message: Message) => {
    if (window.confirm('Are you sure you want to delete this message?')) {
      try {
        await messagingService.deleteMessage(message.id)
        setMessages(prev => prev.map(m => 
          m.id === message.id ? { ...m, isDeleted: true, content: '' } : m
        ))
      } catch (error) {
        console.error('Failed to delete message:', error)
      }
    }
  }

  const handleReplyMessage = (message: Message) => {
    const preview = message.content?.slice(0, 120) || ''
    const event = new CustomEvent('setReplyTo', { detail: { id: message.id, preview } })
    window.dispatchEvent(event)
  }

  const handleReactMessage = async (message: Message, reactionType: 'like' | 'love' | 'wow' | 'sad' | 'angry' | 'haha') => {
    try {
      // Check if user already reacted with this type
      const currentReaction = currentUserReactions[message.id]
      
      if (currentReaction === reactionType) {
        // Remove reaction if clicking same type
        await handleRemoveReaction(message.id)
      } else {
        // Add or change reaction
        await handleReactToMessage(message.id, reactionType)
      }
    } catch (error) {
      console.error('Failed to react to message:', error)
    }
  }

  const getConversationTitle = () => {
    if (conversation.type === ConversationType.DIRECT) {
      return conversation.otherParticipantDisplayName || 'Người dùng';
    }
    return conversation.title || 'Trò chuyện nhóm';
  };

  const getConversationAvatar = () => {
    if (conversation.type === ConversationType.DIRECT) {
      return conversation.otherParticipantAvatarUrl || undefined;
    }
    return conversation.avatar || undefined;
  };

  const isConsecutiveMessage = (currentMessage: Message, previousMessage: Message | undefined) => {
    if (!previousMessage) return false
    return (
      currentMessage.senderId === previousMessage.senderId &&
      new Date(currentMessage.createdAt).getTime() - new Date(previousMessage.createdAt).getTime() < 300000 // 5 minutes
    )
  }

  return (
    <div className={cn("flex flex-col h-full bg-gradient-to-b from-gray-50 to-white", className)}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white/95 backdrop-blur-sm sticky top-0 z-10 shadow-sm">
        <div className="flex items-center space-x-3 flex-1 min-w-0">
          {onBack && (
            <button
              onClick={onBack}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors lg:hidden flex-shrink-0"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
          )}
          
          <div className="flex items-center space-x-3 flex-1 min-w-0">
            {getConversationAvatar() ? (
              <img
                src={getConversationAvatar()}
                alt={getConversationTitle()}
                className="w-10 h-10 rounded-full object-cover"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center">
                <span className="text-white font-semibold">
                  {getConversationTitle().charAt(0).toUpperCase()}
                </span>
              </div>
            )}
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2">
                <h2 className="font-semibold text-gray-900 truncate">{getConversationTitle()}</h2>
                {conversation.type === ConversationType.DIRECT && (
                  <span className={cn(
                    'inline-flex items-center text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0',
                    conversation.participants?.find(p => p.id !== currentUserId)?.isOnline
                      ? 'bg-green-100 text-green-700'
                      : 'bg-gray-100 text-gray-600'
                  )}>
                    <span className={cn(
                      'w-1.5 h-1.5 rounded-full mr-1.5',
                      conversation.participants?.find(p => p.id !== currentUserId)?.isOnline
                        ? 'bg-green-500'
                        : 'bg-gray-400'
                    )}></span>
                    {conversation.participants?.find(p => p.id !== currentUserId)?.isOnline ? 'Trực tuyến' : 'Ngoại tuyến'}
                  </span>
                )}
              </div>
              {conversation.type === ConversationType.GROUP && (
                <div className="mt-1">
                  <GroupMembersDisplay 
                    conversation={{
                      ...conversation,
                      members: groupMembers
                    }} 
                    maxVisible={3}
                    className="cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={() => setShowMembersModal(true)}
                  />
                </div>
              )}
              {wsConnected && conversation.type === ConversationType.DIRECT && (
                <p className="text-xs text-gray-500 mt-0.5">Đã kết nối</p>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-1 flex-shrink-0">
          {wsConnected ? (
            <div className="flex items-center space-x-1 px-2 py-1 bg-green-50 text-green-700 rounded-full text-xs font-medium mr-2">
              <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
              <span>Đã kết nối</span>
            </div>
          ) : (
            <div className="flex items-center space-x-1 px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium mr-2">
              <div className="w-1.5 h-1.5 bg-gray-400 rounded-full"></div>
              <span>Đang kết nối...</span>
            </div>
          )}
          <button className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors" title="Tìm kiếm trong cuộc trò chuyện">
            <Search className="w-5 h-5" />
          </button>
          {conversation.type === ConversationType.GROUP && (
            <button 
              onClick={() => setShowMembersModal(true)}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors" 
              title="Thành viên nhóm"
            >
              <Users className="w-5 h-5" />
            </button>
          )}
          <button className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors" title="Thêm">
            <MoreVertical className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div 
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto px-4 py-6 space-y-3 bg-gradient-to-b from-gray-50/50 to-transparent"
        onScroll={(e) => {
          const { scrollTop } = e.currentTarget
          if (scrollTop === 0 && hasMore) {
            loadMoreMessages()
          }
        }}
      >
        {/* Top loader when fetching older messages */}
        {loading && page > 0 && (
          <div className="flex items-center justify-center py-2">
            <div className="w-full max-w-sm space-y-2">
              <div className="h-3 bg-gray-200 rounded w-24 mx-auto animate-pulse" />
              <div className="h-16 bg-gray-100 rounded animate-pulse" />
            </div>
          </div>
        )}

        {loading && messages.length === 0 ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-32 text-gray-500">
            <p className="text-sm">Chưa có tin nhắn</p>
            <p className="text-xs">Hãy bắt đầu cuộc trò chuyện!</p>
          </div>
        ) : (
          messages.map((message, index) => {
            const prev = messages[index - 1]
            const showDateDivider = !prev || new Date(message.createdAt).toDateString() !== new Date(prev.createdAt).toDateString()
            return (
              <div key={message.id}>
                {showDateDivider && (
                  <div className="flex items-center justify-center py-2">
                    <span className="text-xs text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                      {new Date(message.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                )}
                <MessageBubble
                  message={message}
                  currentUserId={currentUserId}
                  isConsecutive={isConsecutiveMessage(message, prev)}
                  conversationType={conversation.type}
                  onEdit={handleEditMessage}
                  onDelete={handleDeleteMessage}
                  onReply={handleReplyMessage}
                  onReact={handleReactMessage}
                  reactionCounts={reactionCounts[message.id]}
                  currentUserReaction={currentUserReactions[message.id]}
                  isReacting={isReacting[message.id]}
                />
              </div>
            )
          })
        )}
        
        {/* Bottom subtle loader (e.g., after send) */}
        {loading && messages.length > 0 && page === 0 && (
          <div className="flex items-center justify-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500"></div>
          </div>
        )}
        
        {/* Typing Indicator */}
        {propTypingUsers && propTypingUsers.length > 0 && (
          <div className="py-2">
            <TypingIndicator 
              typingUsers={propTypingUsers}
              participants={conversation.type === ConversationType.DIRECT
                ? [
                    {
                      id: conversation.otherParticipantId || 0,
                      displayName: conversation.otherParticipantDisplayName,
                      username: conversation.otherParticipantUsername || ''
                    }
                  ]
                : conversation.participants?.map(p => ({
                    id: p.id,
                    displayName: p.displayName,
                    username: p.username
                  }))}
            />
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Message Input */}
      <div className="bg-white border-t border-gray-200">
        <MessageInput
          onSendMessage={handleSendMessage}
          onStartTyping={() => propOnTyping?.(true)}
          onStopTyping={() => propOnTyping?.(false)}
          disabled={sending}
          placeholder={`Nhập tin nhắn tới ${getConversationTitle()}...`}
        />
      </div>

      {/* Members Modal */}
      {showMembersModal && conversation.type === ConversationType.GROUP && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="relative max-w-md w-full">
            <MemberManagement
              conversation={{
                ...conversation,
                members: groupMembers
              }}
              currentUserId={currentUserId}
              onClose={() => {
                setShowMembersModal(false)
                loadConversationMembers()
              }}
            />
          </div>
        </div>
      )}
    </div>
  )
}
